import React, { useState, useContext, useRef } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { login as loginService } from '../../services/authService';
import { useNavigate } from 'react-router-dom';
import styles from './Login.module.css';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import MenelCaptcha from '../common/MenelCaptcha';
import { useTranslation } from 'react-i18next';

const Login = () => {
  const { t, i18n } = useTranslation();
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [capsOn, setCapsOn] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const pwdRef = useRef(null);

  // Captcha
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaNonce, setCaptchaNonce] = useState(0);

  const canSubmit =
    !busy &&
    email.trim().length > 0 &&
    password.length > 0 &&
    captchaToken &&
    captchaInput.trim().length >= 4;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!canSubmit) {
      setError(t('login.error_complete_captcha'));
      return;
    }

    setBusy(true);
    try {
      const { token, user } = await loginService(
        email.trim(),
        password,
        captchaToken,
        captchaInput.trim()
      );
      if (token && user) {
        login(user, token);
        navigate('/dashboard');
      } else {
        setError(t('login.error_invalid_response'));
      }
    } catch (err) {
      const msg =
        err?.message ||
        err?.response?.data?.message ||
        t('login.error_generic_signin');

      setError(msg);

      // Si el error es de captcha, reset
      if (/captcha/i.test(msg)) {
        setCaptchaToken('');
        setCaptchaInput('');
        setCaptchaNonce((n) => n + 1);
      }
    } finally {
      setBusy(false);
    }
  };

  const onKeyEvent = (e) => {
    if (e.getModifierState) setCapsOn(e.getModifierState('CapsLock'));
  };

  return (
    <div className={styles.shell} dir={i18n.dir()}>
      <div className={styles.card}>
        <div className={styles.logoWrap}>
          <img src="/images/logo.webp" alt="Menelik" className={styles.logo} />
        </div>

        <h1 className={styles.title}>{t('login.title')}</h1>
        <p className={styles.subtitle}>{t('login.subtitle')}</p>

        <form onSubmit={handleSubmit} noValidate className={styles.form}>
          <div className={styles.group}>
            <label htmlFor="email" className={styles.label}>
              {t('login.email_label')}
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className={styles.input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
              required
              placeholder={t('login.email_placeholder')}
              aria-label={t('login.email_aria')}
            />
          </div>

          <div className={styles.group}>
            <label htmlFor="password" className={styles.label}>
              {t('login.password_label')}
            </label>
            <div className={styles.pwdWrap}>
              <input
                id="password"
                ref={pwdRef}
                type={showPwd ? 'text' : 'password'}
                autoComplete="current-password"
                className={styles.input}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyUp={onKeyEvent}
                onKeyDown={onKeyEvent}
                disabled={busy}
                required
                placeholder={t('login.password_placeholder')}
                aria-label={t('login.password_aria')}
              />
              <button
                type="button"
                className={styles.pwdToggle}
                onClick={() => setShowPwd((s) => !s)}
                aria-label={
                  showPwd
                    ? t('login.hide_password_aria')
                    : t('login.show_password_aria')
                }
                tabIndex={-1}
              >
                {showPwd ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {capsOn && (
              <div className={styles.hintWarn} role="status" aria-live="polite">
                {t('login.caps_on')}
              </div>
            )}
          </div>

          {/* CAPTCHA */}
          <div className={styles.group}>
            <MenelCaptcha
              key={captchaNonce}
              onVerify={(token, value) => {
                setCaptchaToken(token || '');
                setCaptchaInput((value || '').trim());
              }}
            />
          </div>

          {error && (
            <div className={styles.alert} role="alert" aria-live="assertive">
              {error}
            </div>
          )}

          <button
            type="submit"
            className={styles.submit}
            disabled={!canSubmit}
            aria-busy={busy ? 'true' : 'false'}
          >
            {busy ? <span className={styles.spinner} aria-hidden /> : t('login.submit')}
          </button>
        </form>

        <div className={styles.footerLinks}>
          <a className={styles.link} href="/forgot-password">
            {t('login.forgot_password')}
          </a>
          <span className={styles.sep}>•</span>
          <a className={styles.link} href="/register">
            {t('login.create_account')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;

